// src/components/SubscriptionButton.tsx
//
// **ネイティブ(iOS/Android)の内課金だけ**を扱うボタン。
//
// 2026-09-29 に web(PayPal)の半分を削除した。理由:
//  - 課金は Stripe 一本に決定済み（司令塔 2026-09-27）。web の購入は
//    StripeSubscriptionButton が担っており、この画面の PayPal ボタンは
//    **sandbox の client ID** で動く壊れた導線だった。
//  - サーバー側の `/paypal/*` を、署名検証が無く誰でも premium を付けられる
//    穴があったため経路ごと削除した。呼び先が無いボタンを残さない。
// web では何も描画しない（購入導線は StripeSubscriptionButton）。
import React, { useState } from 'react';
import { Button, Box, CircularProgress } from '@mui/material';
import ErrorModal from './ErrorModal';
import { useAuth } from '@/contexts/AuthProvider';
import { useDispatch } from 'react-redux';
import { setTemporaryPremium } from '@/store/premiumSlice';
import { Capacitor } from '@capacitor/core';
import { initializePurchases, purchasePackage, restorePurchases } from '@/services/nativePurchaseService';

interface SubscriptionButtonProps {
    /** RevenueCat のパッケージ identifier（例: '$rc_monthly'） */
    nativePackageId?: string;
}

const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({
    nativePackageId = '$rc_monthly',
}) => {
    const { user } = useAuth();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleNativeSubscribe = async () => {
        if (!user) return;
        setIsLoading(true);
        setErrorMessage('');
        try {
            await initializePurchases(user.uid);
            const hasPremium = await purchasePackage(nativePackageId);
            if (hasPremium) dispatch(setTemporaryPremium(true));
        } catch (e: any) {
            if (e?.code !== 'PURCHASE_CANCELLED') {
                setErrorMessage(`購入エラー: ${e.message}`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleNativeRestore = async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            await initializePurchases(user.uid);
            const hasPremium = await restorePurchases();
            if (hasPremium) {
                dispatch(setTemporaryPremium(true));
                alert('購入を復元しました。');
            } else {
                alert('復元できる購入が見つかりませんでした。');
            }
        } catch (e: any) {
            setErrorMessage(`復元エラー: ${e.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // web はここでは何も出さない（Stripe の導線が別にある）
    if (!Capacitor.isNativePlatform()) return null;

    return (
        <>
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNativeSubscribe}
                        sx={{ px: 3, py: 1, fontSize: '1rem' }}
                    >
                        サブスクリプションを開始する
                    </Button>
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleNativeRestore}
                        sx={{ color: 'text.secondary' }}
                    >
                        購入を復元する
                    </Button>
                </Box>
            )}
            {errorMessage && (
                <ErrorModal open={true} message={errorMessage} onClose={() => setErrorMessage('')} />
            )}
        </>
    );
};

export default SubscriptionButton;
